import React, { useEffect, useState, useRef } from 'react';
import usePersistedState from '../utils/useListPreferences';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import { Search, ChevronLeft, ChevronRight, Sparkles, CheckCircle, Filter, Lock, History } from 'lucide-react';
import AIFeedbackDialog from './AiFeedbackDialog';
import EditEssayDialog from './EditEssayDialog';
import secureStorage from '../utils/secureStorage';
import { API_BASE } from '../config/api';
import DifficultyBadge from './DifficultyBadge';
import ForecastStars, { ForecastLegend } from './ForecastStars';

const WritingForecast = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialPart = (() => {
    const param = new URLSearchParams(location.search).get('part');
    return param === 'part2' ? 'part2' : 'part1';
  })();
  const [items, setItems] = useState([]);
  const [liveCounts, setLiveCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Writing VIP only — the local fallback AI-grading quota shown before /ai/writing/quota
  // answers. Listening/Reading (incl. the legacy "all_skills" = L&R bundle) do NOT count.
  const [hasAnyVipForAi, setHasAnyVipForAi] = useState(false);
  const [partSort, setPartSort] = useState(initialPart);
  const [sortOrder, setSortOrder] = useState('default');
  const [selectedType, setSelectedType] = useState('all');
  // Remembered so returning from a part lands back on the page it was on.
  const [currentPage, setCurrentPage] = usePersistedState('listPage:writingForecast', 1);
  const [hideDone, setHideDone] = usePersistedState('hideDone:writingForecast', false, 'local');
  const itemsPerPage = 6;

  const TYPE_LABELS = {
    'line': 'Line',
    'bar': 'Bar',
    'pie': 'Pie',
    'table': 'Table',
    'map': 'Map',
    'process': 'Process',
    'mixed_task1': 'Mixed',
    'agree_disagree': 'Agree or disagree',
    'negative_positive': 'Negative or positive',
    'advantages_disadvantages': 'Advantages outweigh disadvantages',
    'discuss_opinion': 'Discuss both views and give your opinion',
    'solutions_effects': 'Two parts: Solutions - effects',
    'two_parts_mixed': 'Two parts: Mixed'
  };
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [evaluatedMap, setEvaluatedMap] = useState({});
  const [doneExamIds, setDoneExamIds] = useState(new Set());
  const [aiRemaining, setAiRemaining] = useState(0);
  const [aiQuota, setAiQuota] = useState(null); // authoritative server Writing-AI grade quota
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [thumbnails, setThumbnails] = useState({}); // lazy-loaded thumbnail map
  const [histOpen, setHistOpen] = useState(null);   // `${exam_id}-${part_number}` whose history is open
  const [histData, setHistData] = useState({});     // key -> [{attempt_number, band, created_at}]
  const [histLoading, setHistLoading] = useState({});

  const openHist = async (examId, partNumber) => {
    const key = `${examId}-${partNumber}`;
    if (histOpen === key) { setHistOpen(null); return; }
    setHistOpen(key);
    if (histData[key] === undefined) {
      setHistLoading((p) => ({ ...p, [key]: true }));
      try {
        const r = await fetch(`${API_BASE}/student/writing/forecast-history/${examId}/${partNumber}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        });
        const data = r.ok ? await r.json() : [];
        setHistData((p) => ({ ...p, [key]: data }));
      } catch (e) {
        setHistData((p) => ({ ...p, [key]: [] }));
      } finally {
        setHistLoading((p) => ({ ...p, [key]: false }));
      }
    }
  };

  // Sync partSort with ?part= query so navbar links to Task 1 / Task 2 work
  // without remount (e.g., switching tabs while already on this page).
  const appliedPartRef = useRef(null);
  useEffect(() => {
    const param = new URLSearchParams(location.search).get('part');
    const next = param === 'part2' ? 'part2' : 'part1';
    // partSort is already seeded from the URL, so the first run has nothing to change —
    // resetting the page here would wipe the page the student came back to.
    if (appliedPartRef.current === null) {
      appliedPartRef.current = next;
      return;
    }
    if (appliedPartRef.current === next) return;
    appliedPartRef.current = next;
    setPartSort(next);
    setSelectedType('all');
    setCurrentPage(1);
  }, [location.search]);

  // Phase 1: Load list data fast (no images)
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }
      try {
        const [forecastRes, vipRes, historyRes, quotaRes] = await Promise.all([
          fetch(`${API_BASE}/student/writing/forecasts`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_BASE}/customer/vip/subscription/status`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_BASE}/student/writing/history`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_BASE}/ai/writing/quota`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        const [forecastData, vipData] = await Promise.all([forecastRes.json(), vipRes.json()]);
        try {
          const hist = historyRes.ok ? await historyRes.json() : [];
          setDoneExamIds(new Set(hist.map((h) => h.test_id)));
        } catch (e) { /* ignore */ }
        try { if (quotaRes.ok) setAiQuota(await quotaRes.json()); } catch (e) { /* ignore */ }
        setHasAnyVipForAi(!!vipData.has_writing_access);
        const flat = [];
        forecastData.forEach(exam => {
          exam.parts.forEach(p => flat.push({
            task_id: p.task_id,
            part_number: p.part_number,
            difficulty_label: p.difficulty_label || null,
            difficulty_score: p.difficulty_score,
            forecast_level: p.forecast_level || null,
            occurrence_count: p.occurrence_count || 0,
            title: p.title || `Part ${p.part_number}`,
            exam_title: exam.exam_title,
            exam_id: exam.exam_id,
            word_limit: p.word_limit,
            question_types: p.question_types || [],
            band: p.band ?? null
          }));
        });
        setItems(flat);
      } catch (e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  // Live "người đang làm bài" per forecast part (scope = "<exam>p<part>"), refreshed periodically.
  useEffect(() => {
    if (!items.length) return;
    const token = localStorage.getItem('token');
    const scopes = items.map(it => `${it.exam_id}p${it.part_number}`).join(',');
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const res = await fetch(`${API_BASE}/student/live-presence?exam_ids=${scopes}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setLiveCounts(data.counts || {});
        }
      } catch (e) { /* best-effort */ }
    };
    fetchCounts();
    const iv = setInterval(fetchCounts, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [items]);

  // "Ẩn bài đã làm" rides on the same VIP gate as search/sort here.
  // Same rule as isLimitedUser below; spelled out here because that constant is
  // declared after the list is built.
  const canHideDone = !(aiQuota && !aiQuota.is_vip);
  const keepItem = (it) => !(hideDone && canHideDone) || !(it.band != null);

  const base = items.filter(keepItem).filter(it => {
    const matchSearch = (it.title + ' ' + it.exam_title).toLowerCase().includes(searchQuery.toLowerCase());
    const matchType = selectedType === 'all' || (it.question_types || []).includes(selectedType);
    return matchSearch && matchType;
  });
  const sorted = base.filter(it => partSort === 'part1' ? it.part_number === 1 : it.part_number === 2).sort((a, b) => {
    if (sortOrder === 'alphabet_asc') {
      const titleA = a.title || a.exam_title || '';
      const titleB = b.title || b.exam_title || '';
      return titleA.localeCompare(titleB);
    } else if (sortOrder === 'alphabet_desc') {
      const titleA = a.title || a.exam_title || '';
      const titleB = b.title || b.exam_title || '';
      return titleB.localeCompare(titleA);
    } else if (sortOrder === 'difficulty') {
      const av = a.difficulty_score, bv = b.difficulty_score;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return bv - av;
    } else if (sortOrder === 'forecast') {
      return (b.occurrence_count || 0) - (a.occurrence_count || 0);
    }
    return 0; // default order
  });

  const allTypes = [...new Set(items.filter(it => it.part_number === (partSort === 'part1' ? 1 : 2)).flatMap(it => it.question_types || []))].filter(Boolean);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginated = sorted.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sorted.length / itemsPerPage) || 1;

  // A remembered page can point past the end after hiding finished items. Only clamp
  // once the list has actually loaded — on the first render `items` is still empty and
  // totalPages falls back to 1, which would throw away the remembered page.
  useEffect(() => {
    if (items.length > 0 && totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
  }, [items.length, totalPages, currentPage, setCurrentPage]);
  // Free (non-Writing-VIP) customer: first 6 items per tab usable, rest locked —
  // same UX as Reading/Listening forecast (search/sort disabled, upgrade prompt).
  const isLimitedUser = !!(aiQuota && !aiQuota.is_vip);

  // Phase 2: Load visible thumbnails first, then prefetch ALL remaining in background
  const attemptedRef = useRef(new Set());
  const prefetchStartedRef = useRef(false);

  // Helper: fetch a batch of thumbnails
  const fetchThumbBatch = async (taskIds, token) => {
    try {
      const res = await fetch(`${API_BASE}/student/writing/thumbnails`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_ids: taskIds })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.thumbnails && Object.keys(data.thumbnails).length > 0) {
          setThumbnails(prev => ({ ...prev, ...data.thumbnails }));
        }
      }
    } catch (e) { /* non-critical */ }
  };

  // Step 1: Load visible page thumbnails immediately
  const visibleTaskIds = paginated.map(it => it.task_id);
  const visibleKey = visibleTaskIds.join(',');

  useEffect(() => {
    if (!visibleKey) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const ids = visibleKey.split(',').map(Number);
    const needed = ids.filter(id => !attemptedRef.current.has(id));
    if (needed.length === 0) return;
    needed.forEach(id => attemptedRef.current.add(id));
    fetchThumbBatch(needed, token);
  }, [visibleKey]);

  // Step 2: After items load, prefetch ALL remaining thumbnails in background batches
  useEffect(() => {
    if (items.length === 0 || prefetchStartedRef.current) return;
    prefetchStartedRef.current = true;

    const token = localStorage.getItem('token');
    if (!token) return;

    const allTaskIds = items.map(it => it.task_id);

    // Wait 500ms for visible page to load first, then prefetch rest in batches of 6
    const timer = setTimeout(async () => {
      const remaining = allTaskIds.filter(id => !attemptedRef.current.has(id));
      for (let i = 0; i < remaining.length; i += 6) {
        const batch = remaining.slice(i, i + 6);
        batch.forEach(id => attemptedRef.current.add(id));
        await fetchThumbBatch(batch, token);
        // Small delay between batches to avoid overwhelming the server
        if (i + 6 < remaining.length) {
          await new Promise(r => setTimeout(r, 200));
        }
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [items.length]);

  useEffect(() => {
    const role = localStorage.getItem('role');
    const usernameKey = localStorage.getItem('username') || 'unknown';
    const now = new Date();
    const nowUtcMs = now.getTime() + (now.getTimezoneOffset() * 60000);
    const vnMs = nowUtcMs + (7 * 60 * 60 * 1000);
    const vn = new Date(vnMs);
    const dateKey = `${vn.getUTCFullYear()}-${String(vn.getUTCMonth() + 1).padStart(2, '0')}-${String(vn.getUTCDate()).padStart(2, '0')}`;
    const storeKey = `aiEvalCounters:${usernameKey}`;
    const counters = JSON.parse(localStorage.getItem(storeKey) || '{}');
    const isVipOrStudent = hasAnyVipForAi || role === 'student';
    // Unified daily AI-scoring count (shared by full test + forecast): non-VIP = 2/day.
    const limit = isVipOrStudent ? 6 : 2;
    const used = counters[dateKey]?.writing || 0;
    setAiRemaining(Math.max(0, limit - used));
  }, [hasAnyVipForAi, aiDialogOpen, aiLoading]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link to="/" className="text-gray-500 hover:text-[#0096b1]">Home</Link>
              </li>
              <li><span className="text-gray-400 mx-2">/</span></li>
              <li><span className="text-[#0096b1] font-medium">Writing Forecast</span></li>
            </ol>
          </nav>
          <ForecastLegend />
          {aiQuota?.is_vip ? (
            <div className="inline-flex items-center gap-2 rounded-full border border-[#0096b1]/25 bg-[#0096b1]/5 px-4 py-2 self-start sm:self-auto">
              <Sparkles className="w-4 h-4 text-[#0096b1] shrink-0" />
              <span className="text-sm text-gray-600">Chấm điểm AI</span>
              <span className="text-sm font-bold text-[#0096b1]">Không giới hạn</span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 rounded-full border border-[#eb7e37]/30 bg-gradient-to-r from-[#0096b1]/5 to-[#eb7e37]/10 px-4 py-2 self-start sm:self-auto">
              <Sparkles className="w-4 h-4 text-[#eb7e37] shrink-0" />
              <span className="text-sm text-gray-600">Lượt chấm AI hôm nay</span>
              <span className="text-base font-extrabold text-[#eb7e37] tabular-nums">
                {aiQuota ? aiQuota.remaining : aiRemaining}<span className="text-gray-400 font-semibold text-sm">/{aiQuota ? aiQuota.limit : 2}</span>
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={isLimitedUser ? "Tìm kiếm chỉ dành cho VIP..." : "Tìm kiếm dự đoán..."}
              className={`w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-lime-500 focus:border-lime-500 ${isLimitedUser ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              value={searchQuery}
              onChange={(e) => { if (!isLimitedUser) setSearchQuery(e.target.value); }}
              disabled={isLimitedUser}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              className={`px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-lime-500 focus:border-lime-500 font-medium ${isLimitedUser ? 'bg-gray-100 cursor-not-allowed text-gray-400' : 'bg-white text-gray-700'}`}
              value={sortOrder}
              onChange={(e) => { if (!isLimitedUser) setSortOrder(e.target.value); }}
              disabled={isLimitedUser}
              title={isLimitedUser ? 'Sắp xếp chỉ dành cho VIP' : undefined}
            >
              <option value="default">Mới nhất</option>
              <option value="forecast">Theo trúng tủ (cao trước)</option>
              <option value="difficulty">Theo độ khó (dễ trước)</option>
              <option value="alphabet_asc">A-Z</option>
              <option value="alphabet_desc">Z-A</option>
            </select>
            <select
              className="w-full sm:w-32 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-lime-500 focus:border-lime-500 bg-white text-gray-700 font-medium"
              value={partSort}
              onChange={(e) => { setPartSort(e.target.value); setSelectedType('all'); setCurrentPage(1); }}
            >
              <option value="part1">Part 1</option>
              <option value="part2">Part 2</option>
            </select>
          {canHideDone && (
            <label className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg cursor-pointer select-none bg-white hover:bg-gray-50 whitespace-nowrap">
              <input
                type="checkbox"
                className="w-4 h-4 accent-[#0096b1] cursor-pointer"
                checked={hideDone}
                onChange={(e) => { setHideDone(e.target.checked); setCurrentPage(1); }}
              />
              <span className="text-sm font-medium text-gray-700">Ẩn bài đã làm</span>
            </label>
          )}
          </div>
        </div>

        {/* Question Type Filter */}
        {allTypes.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-600">Lọc theo dạng bài:</span>
            </div>
            <div className={`flex flex-wrap gap-2 ${isLimitedUser ? 'opacity-50 pointer-events-none select-none' : ''}`}>
              <button
                onClick={() => { setSelectedType('all'); setCurrentPage(1); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${selectedType === 'all'
                  ? 'bg-[#0096b1] text-white border-[#0096b1]'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-[#0096b1] hover:text-[#0096b1]'
                  }`}
              >
                Tất cả
              </button>
              {allTypes.map(type => {
                const count = items.filter(it => it.part_number === (partSort === 'part1' ? 1 : 2) && (it.question_types || []).includes(type)).length;
                return (
                  <button
                    key={type}
                    onClick={() => { setSelectedType(type); setCurrentPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${selectedType === type
                      ? 'bg-[#0096b1] text-white border-[#0096b1]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#0096b1] hover:text-[#0096b1]'
                      }`}
                  >
                    {TYPE_LABELS[type] || type} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div className="p-8 text-center text-gray-600">Loading...</div>
        ) : sorted.length === 0 ? (
          <div className="p-8 text-center text-gray-600">No forecast items to display</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginated.map((it, index) => {
                const locked = isLimitedUser && (index + indexOfFirstItem) >= 6;
                return (
                <div key={it.task_id} className="bg-white rounded-lg shadow border border-gray-100 p-4 relative">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-800">
                        <span className="text-[#0096b1] italic mr-2">Writing:</span>
                        <span className={locked ? 'blur-[5px] select-none' : ''}>{it.title}</span>
                      </h3>
                      <div className="text-sm text-gray-600 mt-1 flex items-center gap-2">Exam: <span className={locked ? 'blur-[5px] select-none' : ''}>{it.exam_title}</span>
                        <span className="inline-flex items-center gap-2">
                          <DifficultyBadge label={it.difficulty_label} />
                          <ForecastStars level={it.forecast_level} />
                        </span>
                      </div>
                    </div>
                    {!locked && it.band != null && (() => {
                      const key = `${it.exam_id}-${it.part_number}`;
                      return (
                      <div className="relative shrink-0">
                        {histOpen === key && <div className="fixed inset-0 z-40" onClick={() => setHistOpen(null)} />}
                        <button onClick={() => openHist(it.exam_id, it.part_number)} title="Lịch sử làm bài Writing"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-200">
                          <History className="w-4 h-4" /> Lịch sử
                          <ChevronRight className={`w-3.5 h-3.5 transition-transform ${histOpen === key ? 'rotate-90' : ''}`} />
                        </button>
                        {histOpen === key && (() => {
                          const attempts = histData[key] || [];
                          const versions = [];
                          if (it.band != null) versions.push({ key: 'cur', label: 'Lượt hiện tại', band: it.band });
                          attempts.forEach((a) => versions.push({ key: `a${a.attempt_number}`, label: `Lượt ${a.attempt_number}`, band: a.band, date: a.created_at, attempt_number: a.attempt_number }));
                          return (
                            <div className="absolute right-0 mt-2 w-60 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden text-left">
                              <div className="p-3 border-b border-gray-50 bg-gray-50/50"><h4 className="text-sm font-semibold text-gray-700">Lịch sử bài thi</h4></div>
                              <div className="max-h-56 overflow-y-auto">
                                {histLoading[key] ? (
                                  <div className="p-6 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#0096b1] mx-auto" /></div>
                                ) : versions.length > 0 ? (<>
                                  {versions.slice(0, 2).map((v) => (
                                    <button key={v.key} onClick={() => { setHistOpen(null); navigate('/writing_review', { state: { testId: it.exam_id, isForecast: true, attemptNumber: v.attempt_number } }); }}
                                      className="w-full p-3 text-left hover:bg-[#0096b1]/5 border-b border-gray-50 transition-colors">
                                      <div className="flex justify-between items-center">
                                        <span className="text-sm font-semibold text-gray-700">{v.label}</span>
                                        {v.band != null ? <span className="text-base font-bold text-[#0096b1] bg-[#0096b1]/10 px-2.5 py-1 rounded">Band {v.band}</span> : <span className="text-xs text-gray-400">Chưa chấm</span>}
                                      </div>
                                      {v.date && <div className="text-xs text-gray-400 mt-0.5">{new Date(v.date).toLocaleDateString('vi-VN')}</div>}
                                    </button>
                                  ))}
                                  <button onClick={() => { setHistOpen(null); navigate('/exam-history'); }} className="w-full text-center p-3 block text-sm font-semibold text-[#0096b1] hover:bg-[#0096b1]/5 transition-colors">Xem tất cả lịch sử</button>
                                </>) : (<div className="p-4 text-center text-gray-500 text-sm">Chưa có kết quả chấm.</div>)}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      );
                    })()}
                  </div>
                  {liveCounts[`${it.exam_id}p${it.part_number}`] > 0 && (
                    <div className="mt-2 flex items-center gap-1.5 text-[#0096b1] font-semibold text-sm">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0096b1] opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0096b1]"></span>
                      </span>
                      {liveCounts[`${it.exam_id}p${it.part_number}`]} người đang làm bài
                    </div>
                  )}
                  {thumbnails[String(it.task_id)] && (
                    <div className={`mt-2 mb-2 rounded-md overflow-hidden border border-gray-200 bg-gray-50 ${locked ? 'blur-2xl select-none pointer-events-none' : ''}`}>
                      <img
                        src={(() => {
                          const url = thumbnails[String(it.task_id)];
                          return url.startsWith('/') ? `${API_BASE}${url}` : url;
                        })()}
                        alt={`${it.title} preview`}
                        loading="lazy"
                        className="w-full h-36 object-contain transition-opacity duration-300"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}

                  {it.band != null && (
                    <div className={`mt-3 flex items-center justify-center gap-2 rounded-lg bg-[#0096b1]/5 border border-[#0096b1]/20 py-1.5 ${locked ? 'blur-[4px] select-none pointer-events-none' : ''}`}>
                      <span className="text-xs text-gray-500">Điểm của bạn:</span>
                      <span className="text-lg font-extrabold text-[#0096b1]">{it.band}</span>
                    </div>
                  )}
                  {locked ? (
                    <div className="mt-2 flex items-center justify-between gap-2 bg-gray-50 border border-gray-100 rounded px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-gray-600 text-sm font-semibold"><Lock className="w-4 h-4 text-[#0096b1]" /> Cần nâng cấp VIP</span>
                      <Link to="/vip-packages?type=writing" className="px-3 py-1.5 rounded text-xs font-bold text-[#0096b1] bg-white border border-[#0096b1] hover:bg-[#0096b1] hover:text-white transition-colors">Xem gói</Link>
                    </div>
                  ) : (() => {
                    const done = it.band != null || doneExamIds.has(it.exam_id) || evaluatedMap[it.task_id];
                    return (
                      <button
                        onClick={() => navigate('/writing_test_room', { state: { taskId: it.task_id, testId: it.exam_id, isForecast: true, partNumber: it.part_number } })}
                        className={`mt-2 w-full py-2 rounded font-semibold text-white ${done ? 'bg-[#eb7e37] hover:bg-[#d66e2a]' : 'bg-[#0096b1] hover:bg-[#007a90]'}`}
                      >
                        {done ? 'Làm Lại' : 'Làm Bài'}
                      </button>
                    );
                  })()}
                </div>
                );
              })}
            </div>
            <div className="flex justify-center items-center space-x-4 mt-8">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" strokeWidth={3} />
              </button>
              <span className="text-gray-600 font-bold">
                Trang {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" strokeWidth={3} />
              </button>
            </div>
          </>
        )}
        <EditEssayDialog
          isOpen={editDialogOpen}
          onClose={() => { setEditDialogOpen(false); setSelectedPart(null); }}
          taskId={selectedPart}
          partNumber={selectedPart?.part_number}
        />
        <AIFeedbackDialog
          isOpen={aiDialogOpen}
          onClose={() => setAiDialogOpen(false)}
          result={aiResult}
          loading={aiLoading}
          setSelectedPart={setSelectedPart}
          setEditDialogOpen={setEditDialogOpen}
        />
      </div>
    </div>
  );
};

export default WritingForecast;
